import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"
import * as h3 from "h3-js"
import L from "leaflet"

// Fix Leaflet's default icon paths broken by bundlers
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
})

// Heat gradient: cool (low) → hot (high)
// Interpolates through: indigo → cyan → lime → yellow → orange → red
const GRADIENT_STOPS = [
  [99,  102, 241],  // indigo-500
  [6,   182, 212],  // cyan-500
  [132, 204,  22],  // lime-500
  [234, 179,   8],  // yellow-500
  [249, 115,  22],  // orange-500
  [239,  68,  68],  // red-500
]

function lerpColour(ratio) {
  const scaled = ratio * (GRADIENT_STOPS.length - 1)
  const i      = Math.min(Math.floor(scaled), GRADIENT_STOPS.length - 2)
  const f      = scaled - i
  const [r1, g1, b1] = GRADIENT_STOPS[i]
  const [r2, g2, b2] = GRADIENT_STOPS[i + 1]
  const r = Math.round(r1 + (r2 - r1) * f)
  const g = Math.round(g1 + (g2 - g1) * f)
  const b = Math.round(b1 + (b2 - b1) * f)
  return `rgba(${r},${g},${b},`
}

export default class extends Controller {
  static values = {
    points:     Array,
    resolution: { type: Number, default: 7 },
    tileUrl:    { type: String, default: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" }
  }

  connect() {
    this._init()
  }

  disconnect() {
    this.map?.remove()
    this.map = null
  }

  _init() {
    // ── Leaflet map ──────────────────────────────────────────────────────────
    this.map = L.map(this.element, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    })

    L.tileLayer(this.tileUrlValue, {
      attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(this.map)

    const points = this.pointsValue

    if (!points.length) {
      // No data — show world view with a message overlay
      this.map.setView([30, 0], 2)
      this._addNoDataOverlay()
      return
    }

    // ── Bin points into H3 hex indices ────────────────────────────────────
    const hexCounts = {}
    points.forEach(({ lat, lng }) => {
      const idx = h3.latLngToCell(lat, lng, this.resolutionValue)
      hexCounts[idx] = (hexCounts[idx] || 0) + 1
    })

    const counts  = Object.values(hexCounts)
    const maxCount = Math.max(...counts)

    // ── Draw hexagon polygons ─────────────────────────────────────────────
    const hexLayer = L.layerGroup()
    const allLatLngs = []

    Object.entries(hexCounts).forEach(([hexIdx, count]) => {
      const ratio    = maxCount > 1 ? (count - 1) / (maxCount - 1) : 1
      const baseRgba = lerpColour(ratio)
      const fillOpacity = 0.35 + ratio * 0.45  // 35% → 80%
      const fillColor   = baseRgba + fillOpacity + ")"
      const borderColor = baseRgba + Math.min(fillOpacity + 0.3, 1) + ")"

      // h3-js returns [lat, lng] pairs
      const boundary = h3.cellToBoundary(hexIdx)
      const latlngs  = boundary.map(([lat, lng]) => [lat, lng])
      allLatLngs.push(...latlngs)

      const polygon = L.polygon(latlngs, {
        color:       borderColor,
        weight:      1,
        fillColor:   fillColor,
        fillOpacity: 1,
        interactive: true,
      })

      polygon.bindTooltip(
        `<strong>${t("javascript.controllers.hex_map_controller.task_count", { count })}</strong>`,
        { sticky: true, className: "hex-tooltip" }
      )

      hexLayer.addLayer(polygon)
    })

    hexLayer.addTo(this.map)

    // Fit map to hex bounds
    if (allLatLngs.length) {
      this.map.fitBounds(L.latLngBounds(allLatLngs), { padding: [24, 24] })
    }

    // ── Legend ────────────────────────────────────────────────────────────
    this._addLegend(maxCount)
  }

  _addNoDataOverlay() {
    const div = document.createElement("div")
    div.className = "absolute inset-0 flex items-center justify-center z-[1000] pointer-events-none"
    div.innerHTML = `<span class="bg-black/60 text-white text-sm px-4 py-2 rounded-lg">
      ${t("javascript.controllers.hex_map_controller.no_geolocated_tasks")}
    </span>`
    this.element.style.position = "relative"
    this.element.appendChild(div)
  }

  _addLegend(maxCount) {
    const legend = L.control({ position: "bottomright" })
    legend.onAdd = () => {
      const div   = L.DomUtil.create("div", "hex-legend")
      const steps = 5
      let html = `<div style="background:rgba(15,23,42,0.85);padding:10px 12px;border-radius:8px;color:#e2e8f0;font-size:11px;min-width:100px">
        <div style="font-weight:600;margin-bottom:6px;font-size:12px">${t("javascript.controllers.hex_map_controller.task_density")}</div>
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
          <div style="width:80px;height:10px;border-radius:4px;background:linear-gradient(to right,rgba(99,102,241,0.6),rgba(6,182,212,0.7),rgba(132,204,22,0.75),rgba(234,179,8,0.8),rgba(249,115,22,0.85),rgba(239,68,68,0.9))"></div>
        </div>
        <div style="display:flex;justify-content:space-between;width:80px">
          <span>1</span><span>${maxCount}</span>
        </div>
      </div>`
      div.innerHTML = html
      return div
    }
    legend.addTo(this.map)
  }
}
